// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/UniversalTextField/index.jsx

import { Box } from '@modusign/design-system';
import { FontType } from '@modusign/react-signing/types';
import cx from 'classnames';
import { ChangeEvent, Component, createRef, ReactNode } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import { createAbsoluteNumber } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteNumber';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import getFontFaceFontFamily from '@app/features/apply-react-signing/components/FieldRenderer/libs/field-font-family';
import {
  AbsoluteNumber,
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';

import FontSizeNormalizer from './FontSizeNormalizer';
import TextSpaceObserver from './TextSpaceObserver';

import './UniversalTextField.scss';

interface UniversalTextFieldProps {
  text: string;

  point: AbsolutePoint;
  size: AbsoluteSize;
  viewport: AbsoluteSize;

  autoFocus?: boolean;
  dragCancelSelectors?: string[];
  draggable: boolean;
  editable: boolean;
  enableResizeGrid: boolean;
  fontFamily: FontType;

  /**
   * TODO: (@Juyeong-Byeon) 원본 fontSize와 scale을 넘겨서 내부에서 계산하도록 추후에 수정.
   * 현재는 fontsize * scale로 받는 UniversalTextField의 기존 구현 방향을 따름.
   */
  fontSize: AbsoluteNumber;
  fontSizes: AbsoluteNumber[] | null;
  maxFontSize?: AbsoluteNumber;

  resizable: boolean;

  rootClassName: string;
  children?: ReactNode;
  autoCompete?: ReactNode;

  textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  textareaRef: React.RefObject<HTMLElement>;
  onChangeText: (value: string) => void;
  onLineCountChange: (lineCount: number) => void;
  onChangeSize: (absoluteSize: AbsoluteSize) => void;
  onChangePoint: (absolutePoint: AbsolutePoint) => void;
  onBlur: () => void;
  onClick: () => void;
  onFocus: () => void;
  onOutsideClick: (e: MouseEvent) => void;
  onSpaceOverflow: (data: {
    remainSpace: AbsolutePoint;
    isFocusing: boolean;
    isResizing: boolean;
  }) => void;
  onSpaceRemain: (data: {
    remainSpace: AbsolutePoint;
    isFocusing: boolean;
    isResizing: boolean;
  }) => void;
}

interface UniversalTextFieldState {
  isDragging: boolean;
  isFocusing: boolean;
  isResizing: boolean;
  maxFontSize: number;
  resizableBoxKey: string;
}

class UniversalTextField extends Component<UniversalTextFieldProps, UniversalTextFieldState> {
  static getCursorHeight(fontSize: number) {
    // 커서의 높이를 구합니다.
    // cursorHeightRatio는 귀납적으로 산출한 상수입니다. NOTO 폰트와 line-height: 1; 에서 유효합니다.
    const cursorHeightRatio = 1.27;
    return fontSize * cursorHeightRatio;
  }

  static getHeightFromRow(row: number, fontSize: number) {
    return UniversalTextField.getCursorHeight(fontSize) + (row - 1) * fontSize;
  }

  static getRowFromHeight(height: number, fontSize: number) {
    return (height - UniversalTextField.getHeightFromRow(1, fontSize)) / fontSize + 1;
  }

  // height에 들어갈 수 있는 글줄 수를 구해 해당 글줄 수에 딱 맞는 높이로 보정합니다.
  static adjustHeightByFontSizeGrid(height: number, fontSize: number) {
    const rowInHeight = UniversalTextField.getRowFromHeight(height, fontSize);
    return UniversalTextField.getHeightFromRow(
      rowInHeight < 1 ? 1 : Math.round(rowInHeight),
      fontSize
    );
  }

  /**
   * @deprecated
   * universalTextField내부 로직을 이용하기 위해서 기존에 있던 필드를 유지한다.
   * 하지만 이렇게 이전 상태를 관리하는 방법은 적절하지 못해서 추가로 사용하지 않도록 deprecated 플래그를 추가한다.
   * */
  private stableBackupProps: Pick<UniversalTextFieldProps, 'size' | 'point' | 'text' | 'fontSize'>;

  private lineCounterRef = createRef<HTMLDivElement>();

  private prevLineCount: number;

  constructor(props: UniversalTextFieldProps) {
    super(props);
    this.state = {
      isDragging: false,
      isFocusing: false,
      isResizing: false,
      maxFontSize: props.maxFontSize || props.fontSize,
      resizableBoxKey: `${props.size.width}${props.size.height}`,
    };

    this.prevLineCount = 0;

    this.stableBackupProps = props;

    this.handleDragging = this.handleDragging.bind(this);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleResizeStart = this.handleResizeStart.bind(this);
    this.handleResizeStop = this.handleResizeStop.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChangeTextField = this.handleChangeTextField.bind(this);
    this.handleObserveTextSpace = this.handleObserveTextSpace.bind(this);
    this.renderTextarea = this.renderTextarea.bind(this);
  }

  static getDerivedStateFromProps(props: UniversalTextFieldProps, state: UniversalTextFieldState) {
    const newState = {
      ...state,
    };
    if (props.maxFontSize && state.maxFontSize !== props.maxFontSize) {
      newState.maxFontSize = props.maxFontSize;
    }
    return newState;
  }

  componentDidMount() {
    const { autoFocus, textareaRef } = this.props;

    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }

  componentDidUpdate(prevProps: UniversalTextFieldProps) {
    const {
      fontSize,
      enableResizeGrid,
      onChangeSize,
      size,
      onLineCountChange: onLineChange,
    } = this.props;

    // enableResizeGrid가 켜져있을 때 fontSize가 변경되면
    // 크기가 grid에 안맞을 수 있어 그리드에 맞게 보정합니다.
    if (enableResizeGrid && prevProps.fontSize !== fontSize) {
      onChangeSize(
        createAbsoluteSize({
          height: UniversalTextField.adjustHeightByFontSizeGrid(size.height, fontSize),
          width: size.width,
        })
      );
    }

    if (this.lineCounterRef.current) {
      const computedStyle = getComputedStyle(this.lineCounterRef.current);
      const lineCount = Math.floor(
        parseInt(computedStyle.height, 10) / parseInt(computedStyle.lineHeight, 10)
      );

      if (lineCount !== this.prevLineCount) {
        this.prevLineCount = lineCount;
        onLineChange(lineCount);
      }
    }
  }

  handleDragging(newPoint: AbsolutePoint) {
    const { point, onChangePoint, textareaRef } = this.props;
    textareaRef.current?.blur();
    this.setState({ isDragging: true });

    if (newPoint.x !== point.x || newPoint.y !== point.y) {
      // 위치가 변경되면 stableValues의 위치값도 업데이트 합니다.
      this.stableBackupProps = { ...this.stableBackupProps, point: newPoint };
      onChangePoint(newPoint);
    }
  }

  handleDragStop(newPoint: AbsolutePoint) {
    const { point, textareaRef } = this.props;
    this.setState({ isDragging: false });
    // 조금만 드래그한 경우에는 클릭으로 간주하고 focus시킵니다.
    if (Math.abs(newPoint.x - point.x) <= 4 && Math.abs(newPoint.y - point.y) <= 4) {
      textareaRef.current?.focus();
    }
  }

  handleResizeStart() {
    this.setState({ isResizing: true });
  }

  handleResizeStop() {
    const {
      size: { width, height },
    } = this.props;
    this.setState({
      resizableBoxKey: `${width}${height}`,
      isResizing: false,
    });
  }

  handleResize(size: AbsoluteSize) {
    const { enableResizeGrid, fontSize, onChangeSize } = this.props;
    const { width: dataWidth, height: dataHeight } = size;

    const width = parseInt(`${dataWidth}`, 10);
    let height = parseInt(`${dataHeight}`, 10);

    if (enableResizeGrid) {
      // 웹 환경의 한계로 grid의 소숫점이 반올림되어 처리되기 때문에 공간이 남거나 부족해질 수 있어 fontSize로 보정.
      height = UniversalTextField.adjustHeightByFontSizeGrid(height, fontSize);
    }

    onChangeSize(
      createAbsoluteSize({
        width,
        height,
      })
    );
  }

  handleFocus() {
    const { onFocus } = this.props;
    this.setState({ isFocusing: true });
    onFocus();
  }

  handleBlur() {
    const { onBlur } = this.props;
    this.setState({ isFocusing: false });
    onBlur();
  }

  handleChangeTextField(e: ChangeEvent<HTMLTextAreaElement>) {
    const { onChangeText } = this.props;
    onChangeText(e.target.value);
  }

  // <TextSpaceObserver />로 측정한 남는 공간에 따라 fontSize를 조절합니다.
  handleObserveTextSpace(remainSpace: AbsolutePoint) {
    const { fontSize, onSpaceOverflow, onSpaceRemain } = this.props;
    const { isResizing, isFocusing } = this.state;
    /**
     * textarea 한 줄이 가득찬 상태에서 줄의 끝에 공백을 추가하면
     * 실제로 영향을 주지 않지만 넘침으로 판정하는 이슈로 인해
     * 임시로 x축 넘침 검사를 제거합니다.
     *
     * 특정 문자 . , / () 등의 주로 반각문자가 넘침을 일으킬 수 있어서
     * 추후 알맞은 x축 검사 방법을 고민해야될 것 같습니다.
     * @see: https://github.com/modusign/modusign-client/issues/3790
     */
    const isOverflow = remainSpace.y < 0;

    // 넘치지않았을 경우 해당 values를 stableValues로 저장해둡니다.
    if (isOverflow) {
      onSpaceOverflow({
        remainSpace,
        isResizing,
        isFocusing,
      });
    } else {
      this.stableBackupProps = this.props;
      onSpaceRemain({
        remainSpace,
        isResizing,
        isFocusing,
      });
    }

    if (isOverflow && (isResizing || isFocusing) && this.stableBackupProps.fontSize === fontSize) {
      this.revertToStableProps();
    }
  }

  getMaxConstraints(): [number, number] | undefined {
    const {
      point: { x, y },
      viewport: { width, height },
    } = this.props;

    return [width - x, height - y];
  }

  getBiggerFontSize(fontSize: AbsoluteNumber) {
    const { fontSizes } = this.props;

    if (fontSizes) {
      return fontSizes[fontSizes.indexOf(fontSize) + 1] || fontSizes[fontSizes.length - 1];
    }
    return createAbsoluteNumber(fontSize + 1);
  }

  revertToStableProps() {
    const { point, text, size } = this.stableBackupProps;
    const { onChangeText, onChangePoint, onChangeSize } = this.props;
    onChangeText(text);
    onChangePoint(point);
    onChangeSize(size);
  }

  renderTextarea(fontSizeStyle: React.CSSProperties) {
    const { text, editable, fontFamily, textareaProps } = this.props;

    const { isResizing, isDragging } = this.state;

    return (
      <>
        <textarea
          {...textareaProps}
          /**
           *
           * Windows 환경의 Chrome에서 textarea에서 동적으로 autocomplete='on'|"off"를 적용하면
           * 한글의 자모음이 분리되는 버그가 있어 autocomplete='off'로 강제 지정합니다.
           * FYI - https://bugs.chromium.org/p/chromium/issues/detail?id=1500145&q=textarea&can=2
           */
          autoComplete="off"
          className={cx('UniversalTextField__textarea', textareaProps.className)}
          data-testid="text-field"
          readOnly={!editable || isResizing || isDragging}
          style={{
            fontFamily: getFontFaceFontFamily(fontFamily),
            lineHeight: 1,
            ...textareaProps.style,
            ...fontSizeStyle,
          }}
          value={text}
          onBlur={this.handleBlur}
          onChange={this.handleChangeTextField}
          onFocus={this.handleFocus}
        />
        <div
          aria-hidden="true"
          className={cx('UniversalTextField__textarea', textareaProps.className)}
          ref={this.lineCounterRef}
          style={{
            fontFamily: getFontFaceFontFamily(fontFamily),
            lineHeight: 1,
            ...textareaProps.style,
            ...fontSizeStyle,
            height: 'auto',
            position: 'absolute',
            whiteSpace: 'pre-wrap',
            visibility: 'hidden',
          }}
        >
          {text}
        </div>
      </>
    );
  }

  render() {
    const {
      point,
      size,
      text,
      fontSize,
      children,
      autoCompete,
      dragCancelSelectors,
      draggable,
      enableResizeGrid,
      fontFamily,
      resizable,
      rootClassName,
      onClick,
      onOutsideClick,
      viewport,
    } = this.props;
    const { isDragging, isResizing, isFocusing, resizableBoxKey } = this.state;

    const className = cx({
      UniversalTextField: true,
      'UniversalTextField--dragging': isDragging,
      'UniversalTextField--focusing': isFocusing,
      [rootClassName]: rootClassName,
    });

    return (
      <>
        <FieldBase
          defaultClassName={className}
          disabled={!draggable || (!!text && isFocusing)}
          dragCancelSelectors={dragCancelSelectors}
          enableUserSelectHack={false}
          fieldGuideline={{
            guideLineOffset: {
              left: 3,
              top: 3,
              right: 2,
              bottom: 2,
            },
            viewport,
            showGuideLine: isDragging || isResizing,
          }}
          key={resizableBoxKey}
          maxConstraints={this.getMaxConstraints()}
          minConstraints={[fontSize, UniversalTextField.getHeightFromRow(1, fontSize)]}
          point={point}
          readOnly={!resizable}
          size={size}
          snapSize={
            enableResizeGrid ? createAbsoluteSize({ width: 1, height: fontSize }) : undefined
          }
          onPointChange={this.handleDragging}
          onPointChangeStop={this.handleDragStop}
          onSizeChange={this.handleResize}
          onSizeChangeStart={this.handleResizeStart}
          onSizeChangeStop={this.handleResizeStop}
        >
          <OutsideClickHandler onOutsideClick={onOutsideClick}>
            {autoCompete}
            <Box
              lineHeight={0}
              style={{
                width: `${size.width}px`,
                height: `${size.height}px`,
              }}
            >
              <Box className="UniversalTextField__box" height="100%" onClick={onClick}>
                <FontSizeNormalizer fontSize={fontSize} render={this.renderTextarea} />
              </Box>
              {children}
            </Box>
          </OutsideClickHandler>
        </FieldBase>
        <Box className="UniversalTextField__observer-box">
          <TextSpaceObserver
            fontSizes={[fontSize, this.getBiggerFontSize(fontSize)]}
            style={{
              fontFamily: getFontFaceFontFamily(fontFamily),
              height: size.height,
              lineHeight: 1,
              textAlign: 'left',
              width: size.width,
              letterSpacing: 0,
            }}
            text={text}
            onObserve={this.handleObserveTextSpace}
          />
        </Box>
      </>
    );
  }
}

export default UniversalTextField;
